interface Bounds { left: number; top: number; right: number; bottom: number }
const imageBounds = new WeakMap<HTMLImageElement, { src: string; bounds: Bounds | null }>()

function alphaBounds(image: HTMLImageElement): Bounds | null {
  if (!image.complete || !image.naturalWidth || !image.naturalHeight) return null
  const cached = imageBounds.get(image)
  if (cached?.src === image.currentSrc) return cached.bounds
  let bounds: Bounds | null = null
  try {
    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (context) {
      context.drawImage(image, 0, 0)
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data
      let left = canvas.width, top = canvas.height, right = 0, bottom = 0
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          if (pixels[(y * canvas.width + x) * 4 + 3] === 0) continue
          left = Math.min(left, x); right = Math.max(right, x + 1)
          top = Math.min(top, y); bottom = Math.max(bottom, y + 1)
        }
      }
      if (right > left && bottom > top) bounds = { left, top, right, bottom }
    }
    // Release the pixel buffer after the one-time alpha scan.
    canvas.width = canvas.height = 0
  } catch {
    // Cross-origin uploads without canvas access retain their image rectangle.
  }
  imageBounds.set(image, { src: image.currentSrc, bounds })
  return bounds
}

/** Visible content bounds in canvas pixels, excluding transparent padding. */
export function measurePromptBounds(element: HTMLElement, layoutWidth: number): Bounds {
  const rect = element.getBoundingClientRect()
  const scale = rect.width / layoutWidth || 1
  const rectangles: Bounds[] = []
  const include = (box: Bounds) => rectangles.push({
    left: (box.left - rect.left) / scale, top: (box.top - rect.top) / scale,
    right: (box.right - rect.left) / scale, bottom: (box.bottom - rect.top) / scale,
  })
  const panel = element.querySelector<HTMLElement>('[data-prompt-panel]')
  if (panel && Number(getComputedStyle(panel).opacity) > 0) {
    include(panel.getBoundingClientRect())
  } else {
    const artwork = element.querySelector<HTMLElement>('[data-prompt-artwork]')
    const svg = artwork?.querySelector('svg')
    if (artwork && svg && Number(getComputedStyle(artwork).opacity) > 0) {
      const box = svg.getBBox()
      const matrix = svg.getScreenCTM()
      if (matrix) {
        const a = new DOMPoint(box.x, box.y).matrixTransform(matrix)
        const b = new DOMPoint(box.x + box.width, box.y + box.height).matrixTransform(matrix)
        include({ left: a.x, top: a.y, right: b.x, bottom: b.y })
      }
    }
    const text = element.querySelector<HTMLElement>('[data-prompt-text]')
    if (text && text.parentElement && Number(getComputedStyle(text.parentElement).opacity) > 0) include(text.getBoundingClientRect())
    const accent = element.querySelector<HTMLElement>('[data-prompt-accent]')
    if (accent && Number(getComputedStyle(accent).opacity) > 0) include(accent.getBoundingClientRect())
  }
  const image = element.querySelector<HTMLImageElement>('[data-prompt-image]')
  if (image && Number(getComputedStyle(image).opacity) > 0) {
    const box = image.getBoundingClientRect()
    const alpha = alphaBounds(image)
    if (alpha) {
      const factor = Math.min(box.width / image.naturalWidth, box.height / image.naturalHeight)
      const left = box.left + (box.width - image.naturalWidth * factor) / 2
      const top = box.top + (box.height - image.naturalHeight * factor) / 2
      include({ left: left + alpha.left * factor, top: top + alpha.top * factor, right: left + alpha.right * factor, bottom: top + alpha.bottom * factor })
    } else include(box)
  }
  return rectangles.length ? {
    left: Math.min(...rectangles.map(b => b.left)), top: Math.min(...rectangles.map(b => b.top)),
    right: Math.max(...rectangles.map(b => b.right)), bottom: Math.max(...rectangles.map(b => b.bottom)),
  } : { left: 0, top: 0, right: layoutWidth, bottom: rect.height / scale }
}
